import React from "react";
import type { Tier } from "@/types/cred";
import { twMerge } from "tailwind-merge";

type TierType = Tier["tier"];

///NEEDS TO BE EXPORTED TO FRIENDS LIST BECUASE THAT API POINT DOESNT RETURN BOOTS//
export const LOYALTY_TIERS: { minScore: number; type: TierType }[] = [
  { minScore: 2.0, type: "Bronze" },
  { minScore: 2.5, type: "Silver" },
  { minScore: 3.0, type: "Gold" },
  { minScore: 3.5, type: "Onyx" },
  { minScore: 4.0, type: "Diamond" },
  { minScore: 4.5, type: "Mythic" },
  { minScore: 5.0, type: "Ethereal" },
];

interface LoyaltyBadgeProps {
  boost: number;
  size?: "sm" | "md" | "lg";
  className?: string;
}

interface TierImageProps {
  tier: TierType;
  className?: string;
  size?: number;
}

export const TierImage = ({ tier, className, size = 112 }: TierImageProps) => {
  const safeTier = tier?.toLowerCase() || "";

  return (
    <div className={twMerge("relative rounded-full", className)} style={{ width: size, height: size }}>
      <img
        src={`/assets/img/cred/metal/${safeTier}.png`}
        alt={`${tier || "Bronze"} tier badge`}
        width={size}
        height={size}
        className="rounded-full"
        onError={e => {
          e.currentTarget.style.display = "none";
          e.currentTarget.nextElementSibling?.classList.remove("hidden");
        }}
      />
      <div className="absolute inset-0 hidden rounded-full border border-neutral-200 bg-neutral-100/10" style={{ width: size, height: size }} />
    </div>
  );
};

export default function LoyaltyBadge({ boost, size = "md", className }: LoyaltyBadgeProps) {
  const getCurrentTier = (boost: number): TierType => {
    const tier = [...LOYALTY_TIERS].reverse().find(tier => boost >= tier.minScore);
    return tier?.type || "Bronze";
  };

  const sizeClasses = {
    sm: "w-16 h-16",
    md: "w-20 h-20",
    lg: "w-24 h-24",
  };

  const boostTextClasses = {
    sm: "text-lg",
    md: "text-xl",
    lg: "text-2xl",
  };

  const tierType = getCurrentTier(boost);

  return (
    <div className={twMerge(`relative ${sizeClasses[size]}`, className)}>
      <div className={`absolute inset-0 z-10 flex items-center justify-center font-mono font-bold text-neutral-25 [text-shadow:0px_0px_4.8px_#FCFCFD] ${boostTextClasses[size]}`}>
        {boost}X
      </div>
      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="-4 -4 112 126" fill="none" className="h-full w-full">
        <mask id={`badge-mask-${boost}`}>
          <path
            d="M61.1205 3.39628C56.095 0.496964 49.905 0.496965 44.8795 3.39628L8.87951 24.1655C3.8492 27.0676 0.75 32.4336 0.75 38.241V79.759C0.75 85.5664 3.8492 90.9324 8.87951 93.8345L44.8795 114.604C49.905 117.503 56.095 117.503 61.1205 114.604L97.1205 93.8345C102.151 90.9324 105.25 85.5664 105.25 79.759V38.241C105.25 32.4336 102.151 27.0676 97.1205 24.1655L61.1205 3.39628Z"
            fill="white"
            stroke="#404040"
            strokeWidth="0.5"
          />
        </mask>

        <image href={`/assets/img/cred/metal/${tierType.toLowerCase()}.png`} x="-4" y="-4" width="112" height="126" mask={`url(#badge-mask-${boost})`} />

        <path
          d="M61.1205 3.39628C56.095 0.496964 49.905 0.496965 44.8795 3.39628L8.87951 24.1655C3.8492 27.0676 0.75 32.4336 0.75 38.241V79.759C0.75 85.5664 3.8492 90.9324 8.87951 93.8345L44.8795 114.604C49.905 117.503 56.095 117.503 61.1205 114.604L97.1205 93.8345C102.151 90.9324 105.25 85.5664 105.25 79.759V38.241C105.25 32.4336 102.151 27.0676 97.1205 24.1655L61.1205 3.39628Z"
          stroke="#404040"
          strokeWidth="0.5"
        />
      </svg>
    </div>
  );
}

export const LoyaltyBadgeNone = ({ className }: { className?: string }) => {
  return (
    <div className={className}>
      <svg xmlns="http://www.w3.org/2000/svg" width="56" height="60" viewBox="0 0 64 70" fill="none">
        <path
          d="M36.5723 1.63777C33.557 -0.101821 29.843 -0.101822 26.8277 1.63777L5.22771 14.0993C2.20952 15.8406 0.35 19.0602 0.35 22.5446V47.4554C0.35 50.9398 2.20952 54.1594 5.2277 55.9007L26.8277 68.3622C29.843 70.1018 33.557 70.1018 36.5723 68.3622L58.1723 55.9007C61.1905 54.1594 63.05 50.9398 63.05 47.4554V22.5446C63.05 19.0602 61.1905 15.8406 58.1723 14.0993L36.5723 1.63777Z"
          fill="#8979D9"
          fillOpacity="0.1"
          stroke="#635A8A"
          strokeWidth="0.3"
        />
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 44 44" fill="none" x="10" y="11">
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M34.5556 10.4319C35.2997 10.4952 36.0628 10.4322 36.8142 10.2309C36.3393 10.8472 35.9782 11.524 35.7326 12.2325C35.2796 13.5388 35.2188 14.9528 35.5587 16.2938C35.7375 16.9993 36.0272 17.6847 36.429 18.3235L36.5029 18.4409C36.4498 18.419 36.3965 18.3979 36.3431 18.3776C35.65 18.1133 34.9323 17.9754 34.2191 17.956C32.9651 17.9218 31.7249 18.2542 30.656 18.9107C30.0481 19.2841 29.4955 19.7624 29.0274 20.3378C28.9913 20.3821 28.9557 20.4271 28.9207 20.4725L28.9259 20.334C28.9545 19.5798 28.8628 18.8414 28.6648 18.141C28.2886 16.8097 27.529 15.6156 26.4836 14.7108C25.9166 14.22 25.2655 13.8144 24.546 13.5182C25.2974 13.3168 25.9898 12.9898 26.6025 12.5629C27.6669 11.8213 28.4907 10.7786 28.9656 9.57044C29.2253 8.90984 29.3806 8.1998 29.4139 7.46253L29.4159 7.41466L29.4245 7.18848L29.545 7.38007L29.5707 7.42053C29.9682 8.04238 30.4577 8.57962 31.0129 9.02188C32.0282 9.83071 33.2631 10.3218 34.5556 10.4319ZM33.8693 12.1627C32.6231 11.9754 31.4332 11.5088 30.392 10.7991C29.845 11.9343 29.0479 12.9334 28.0624 13.7187C29.0274 14.6636 29.7656 15.8181 30.2184 17.0909C31.2924 16.5311 32.4782 16.2133 33.6882 16.1611C33.4439 14.8325 33.506 13.4636 33.8693 12.1627Z"
            fill="#2E2944"
          />
          <path d="M17.4496 11.7725L23.1308 14.805L22.2831 16.3929L19.2496 14.7737V21.6591H22.4291V23.4591H19.2496V31.2731H17.4496V11.7725Z" fill="#2E2944" />
          <path d="M24.673 17.6923L23.6119 17.1538L24.4266 15.5487L27.8329 17.2774L27.0183 18.8826L26.473 18.6058V28.0304H24.673V17.6923Z" fill="#2E2944" />
          <path d="M16.2787 15.642V30.4432L11.9872 23.3763V27.8853H10.1872V16.9445L14.4787 24.0113V15.642H16.2787Z" fill="#2E2944" />
          <path
            d="M19.2171 36.8114L31.7872 29.076V20.4731H29.9872V28.0702L19.1572 34.7348L8.38716 28.9355L8.38716 15.6107L19.2025 9.78701L24.1847 12.2781L24.9897 10.6681L19.1718 7.75919L6.58716 14.5355V30.0107L19.2171 36.8114Z"
            fill="#2E2944"
          />
        </svg>
      </svg>
    </div>
  );
};
